/* eslint-disable no-param-reassign */
/* eslint-disable class-methods-use-this */

import Component from '@bythepixel/component-loader';
import FormSelect from '../formSelect';
import {
  convertObjectToArray,
  convertOptionsToArrayOfObjects,
  goToPage,
  mapQuery,
} from './utils';

export default class VehicleSearch extends Component {
  static selector = '[data-comp-vehicle-search]';

  API_ENDPOINT = `${window.location.origin}/api/search/hero-dropdown/`;
  BASE_URL = window.location.origin;

  constructor(container, options = {}) {
    super(container, options);

    this.dropdowns = this.bindDropdowns();

    this.groupId = this.$container.dataset.groupId;
    this.industryId = this.$container.dataset.industryId;
    this.makeId = this.$container.dataset.makeId;
    this.pageType = this.getPageType();
    this.vehicleTypeId = this.$container.dataset.vehicleTypeId;
    this.submitButton = this.$container.querySelector('button[type="submit"]');
    this.filterAttributes = this.$container.dataset.filterAttributes;

    this.isHorseLivestockTrailersPage = window.location.href.indexOf('horse-livestock-trailers') > -1;
    this.isMakeAndModelFilter = this.dropdowns[0].el.id === 'make' && this.dropdowns[1].el.id === 'model';

    this.registerListeners();

    // Ensures that when using the back button, the dropdowns are reset
    this.restoreOriginalDropdownOptions(this.dropdowns[0]);
    this.restoreOriginalDropdownOptions(this.dropdowns[1]);
  }

  getPageType() {
    if (this.industryId) return 'industry';
    if (this.makeId) return 'make';
    if (this.vehicleTypeId) return 'vehicleType';

    return null;
  }

  bindDropdowns() {
    const selectOneWrapper = this.$container.querySelector(
      '[data-comp-vehicle-search-select-one-wrapper]',
    );
    const selectTwoWrapper = this.$container.querySelector(
      '[data-comp-vehicle-search-select-two-wrapper]',
    );

    const dropdowns = [selectOneWrapper, selectTwoWrapper].filter(
      (dropdown) => dropdown,
    );

    return dropdowns.map((wrapper) => {
      const el = wrapper.querySelector('[data-comp-select');

      return {
        el,
        instance: new FormSelect(wrapper),
        options: convertOptionsToArrayOfObjects([...el.options]),
        type: el.id.toLowerCase(),
        wrapper,
      };
    });
  }

  /**
   * Getter to determine whether both dropdowns should reset, which should
   * happen if the first filter doesn't have a value while the second filter does
   *
   * @returns { bool }
   */
  get dropdownsShouldReset() {
    const [dropdownOne, dropdownTwo] = this.dropdowns;

    return !dropdownOne.el.value && dropdownTwo.el.value;
  }

  /**
   * Getter to determine whether both dropdowns are empty
   *
   * @returns { bool }
   */
  get bothDropdownsEmpty() {
    const [dropdownOne, dropdownTwo] = this.dropdowns;

    return !dropdownOne.el.value && !dropdownTwo.el.value;
  }

  get secondDropdownShouldDisable() {
    return this.isMakeAndModelFilter && this.bothDropdownsEmpty;
  }

  /**
   * Finds duplicate options in a dropdown and removes the first duplicate
   * in favor of a newly-populated duplicate
   */
  resolveDuplicateOptions(dropdown, optionValue) {
    // find the index of the duplicate <option> we want to serve as our new
    // selected option.
    const duplicateOptions = [...dropdown.el.options].filter(
      (option) => option.value === optionValue,
    );
    const newSelectIndex = duplicateOptions.pop().index;

    // update to new selection
    dropdown.el.options.selectedIndex = newSelectIndex;

    // remove unneeded duplicate
    dropdown.el.options[1] = undefined;
  }

  populateDropdown(targetDropdown, options) {
    options.forEach((option) => targetDropdown.el.options.add(option));
  }

  buildOptionElement(option) {
    const optionEl = new Option(option.display_name || option.name, option.id);
    optionEl.dataset.slug = option.slug;
    optionEl.dataset.name = option.name;

    return optionEl;
  }

  /**
   * Resets a dropdown's options and FormSelect instance
   *
   * @returns { void }
   */
  resetDropdown(dropdown, optionToKeepByValue) {
    const keepCurrentSelection = optionToKeepByValue !== '';

    if (!keepCurrentSelection) {
      dropdown.instance.resetSelectState();
    }

    const selectableOptionCount = dropdown.el.options.length - 1;

    for (let i = selectableOptionCount; i >= 1; i -= 1) {
      const optionValue = dropdown.el.options[i].value;

      if (optionToKeepByValue !== optionValue) {
        dropdown.el.options[i] = undefined;
      }
    }
  }

  restoreOriginalDropdownOptions(dropdown, keepCurrentSelection = false) {
    const optionToKeep = keepCurrentSelection ? dropdown.el.value : '';

    this.resetDropdown(dropdown, optionToKeep);

    dropdown.options
      .map((option) => this.buildOptionElement(option))
      .forEach((option) => dropdown.el.options.add(option));

    if (keepCurrentSelection) {
      this.resolveDuplicateOptions(dropdown, optionToKeep);
    }
  }

  restoreDropdowns() {
    const dropdownTwo = this.dropdowns[1];
    this.dropdowns.forEach((dropdown) => this.restoreOriginalDropdownOptions(dropdown));
    dropdownTwo.el.disabled = this.secondDropdownShouldDisable;
  }

  buildQueryParamsFromDropdowns() {
    return this.dropdowns.map((dropdown) => mapQuery(dropdown.el));
  }

  async fetchAndPopulateDropdown(targetDropdown) {
    const dropdownTwo = this.dropdowns[1];
    const currentDropdown = this.dropdowns.find(
      (dropdown) => dropdown.el !== targetDropdown.el,
    );

    // if our targetDropdown already has a value while we're updating its options,
    // we want to keep that existing selection
    const optionToKeep = targetDropdown.el.value || '';
    this.resetDropdown(targetDropdown, optionToKeep);

    const requestBody = {
      group: this.groupId,
      has: currentDropdown.type,
      value: currentDropdown.el.value,
      wants: targetDropdown.type,
    };

    if (this.filterAttributes) {
      requestBody.filterAttributes = this.filterAttributes;
    }

    if (this.pageType) {
      requestBody.prefilter = this.pageType;

      const prefilterValuePageTypeMapping = {
        industry: this.industryId,
        make: this.makeId,
        vehicleType: this.vehicleTypeId,
      };

      requestBody.prefilter_value = prefilterValuePageTypeMapping[this.pageType];
    }

    const query = new URLSearchParams(requestBody).toString();
    const response = await fetch(`${this.API_ENDPOINT}?${query}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    });

    const data = await response.json();
    const options = convertObjectToArray(data).map((option) => this.buildOptionElement(option));

    this.populateDropdown(targetDropdown, options);

    // if we're keeping an existing selection, we need to filter it out of
    // our newly-built options
    if (optionToKeep) {
      this.resolveDuplicateOptions(targetDropdown, optionToKeep);
    }

    dropdownTwo.el.disabled = options.length === 0 || this.secondDropdownShouldDisable;
  }

  async handleInput(event) {
    const { currentTarget, detail } = event;
    const targetDropdown = this.dropdowns.find(
      (dropdown) => dropdown.el !== currentTarget,
    );

    if (!detail?.inputCleared) {
      await this.fetchAndPopulateDropdown(targetDropdown);
      return;
    }

    if (this.dropdownsShouldReset || this.bothDropdownsEmpty) {
      this.restoreDropdowns();
      return;
    }

    // if it's an input being cleared and at least one input still
    // has a value, we need to restore the targetDropdown's original options
    if (!this.bothDropdownsEmpty) {
      this.restoreOriginalDropdownOptions(targetDropdown, true);
    }
  }

  handleSubmit(event) {
    event.preventDefault();

    // initialize storeUrl
    const storeUrl = new URL(this.$container.action, this.BASE_URL);

    // go to base store url if no filters are set
    if (this.bothDropdownsEmpty) {
      goToPage(storeUrl.href);
      return;
    }

    const params = this.buildQueryParamsFromDropdowns();

    // if we're not on a horse and livestock page, finish building
    // our URL and go to it
    if (!this.isHorseLivestockTrailersPage) {
      params.forEach((param) => {
        if (!param) {
          return;
        }

        storeUrl.searchParams.append(param.key, param.value);
      });

      goToPage(decodeURIComponent(storeUrl.href));
      return;
    }

    const horseAndLivestockMapping = {
      'horse-trailer': '/horse-trailers/type/all/store/',
      'livestock-trailer': '/livestock-trailers/type/all/store/',
      'stock-box': '/livestock-trailers/type/stock-box/store/',
      default: '/horse-livestock-trailers/type/all/store/',
    };

    const targetSlug = this.dropdowns[0].el.selectedOptions[0].dataset.slug;

    storeUrl.pathName = horseAndLivestockMapping[targetSlug] || horseAndLivestockMapping.default;

    if (params[1]) {
      storeUrl.searchParams.append(params[1].key, params[1].value);
    }

    goToPage(decodeURIComponent(storeUrl.href));
  }

  registerListeners() {
    this.dropdowns.forEach((dropdown) => {
      dropdown.el.addEventListener('input', async (e) => this.handleInput(e));
    });

    this.$container.addEventListener('submit', this.handleSubmit.bind(this));
  }
}
